import React from 'react';

import { FormField, Label, Listbox, Paragraph } from '@zillow/constellation';

import type { Section } from '../../../models/index';
import { DEATH_IN_PROPERTY } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function DeathInProperty({ section, setSectionValues }: Props) {
  function onDeathOnPropertyYesClick(): void {
    setSectionValues({
      [DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH]: false,
    });
  }

  function onDeathOnPropertyNoClick(): void {
    setSectionValues({
      [DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH]: true,
      [DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE]: false,
      [DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME]: false,
      [DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE]: false,
    });
  }

  function onOptionSelect(option: { id: string; value: string; label: string }): void {
    setSectionValues({
      [option.value]: !section.getFieldValue(option.value),
    });
  }

  const listboxValues = [];
  const naturalCauses = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE);
  const violentCrime = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME);
  const communicableDisease = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE);

  if (naturalCauses) {
    listboxValues.push(DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE);
  }
  if (violentCrime) {
    listboxValues.push(DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME);
  }
  if (communicableDisease) {
    listboxValues.push(DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE);
  }

  const noDeathInProperty = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH);

  return (
    <FormField
      label={
        <>
          <Label size="lg">Are you aware of any death(s) in the property?</Label>
          <Paragraph>
            You must disclose all forms of deaths in property. The tenant has a right to have questions answered
            pertaining to death in the rental unit to the best of the landlord’s knowledge upon request.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion
          // Since the variable is referring to the opposite, this will take care of that case.
          yesActive={noDeathInProperty === null ? null : !noDeathInProperty}
          yesHandler={onDeathOnPropertyYesClick}
          noHandler={onDeathOnPropertyNoClick}
        >
          <FormField
            label={<Label>How did the death(s) happen? (check all that apply)</Label>}
            control={
              <Listbox
                value={listboxValues}
                onOptionSelect={onOptionSelect}
                options={[
                  { value: DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE, label: 'By natural causes' },
                  { value: DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME, label: 'By violent crime' },
                  {
                    value: DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE,
                    label: 'By communicable disease',
                    meta: 'All communicable diseases should be included with the exception of deaths related to HIV or AIDS, which are protected under state statute.',
                  },
                ]}
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

DeathInProperty.description = 'Asks Irene whether or not there was a death in the property';

export default DeathInProperty;
